import SyosEvents from './utils/SyosEvents';
import SyosUiContainer from './SyosUiContainer';
import SyosSeat from './SyosSeat';
import Viewport from './utils/Viewport';
import SyosGlobalConfig from './SyosGlobalConfig';
import SyosSeatMap from './SyosSeatMap';
import StageOrientation, { getOrientationFromSeatValue } from './StageOrientation';
import SeatClientRectProvider from './utils/SeatClientRectProvider';

const HTML = `
  <button class="tn-syos-btn-toggle-full-screen" type="button">
    <span class="tn-syos-btn-toggle-full-screen__label-enter">
      <i class="fa fa-expand" aria-hidden="true"></i>
    </span>
    <span class="tn-syos-btn-toggle-full-screen__label-exit">
      <i class="fa fa-compress" aria-hidden="true"></i>
    </span>
  </button>
`;

const autoSelectSeatQuantity = 0;

/**
 * Takes an array "raw" API seat data, sorts out seats from
 * non-seats, and adjusts all coordinates based on stage
 * indicator settings
 *
 * @param {Array} apiSeatList
 */
function adjustSeatCoordinates(apiSeatList) {
  const seats = [];
  const nonSeats = [];
  const { renderNonSeats } = SyosGlobalConfig.seatView;
  const stageOrientation = getOrientationFromSeatValue(apiSeatList[0].StageOrientation);

  let minX = Number.MAX_SAFE_INTEGER;
  let minY = Number.MAX_SAFE_INTEGER;
  let maxX = -Number.MAX_SAFE_INTEGER;
  let maxY = -Number.MAX_SAFE_INTEGER;

  apiSeatList.forEach((data) => {
    let isSeat = false;
    let isNonSeat = false;

    if (data.is_seat === true) {
      isSeat = true;
    }

    if (
      renderNonSeats &&
      data.seat_status_desc === 'NonSeat' &&
      data.display_letter &&
      data.display_letter.length === 1
    ) {
      isNonSeat = true;
    }

    if (isSeat || isNonSeat) {
      minX = Math.min(minX, data.xpos);
      minY = Math.min(minY, data.ypos);
      maxX = Math.max(maxX, data.xpos);
      maxY = Math.max(maxY, data.ypos);

      const collection = isSeat ? seats : nonSeats;
      collection.push(data);
    }
  });

  // How much in the x or y axis the stage offsets seat coordinates:
  const STAGE_GRID_OFFSET = 3;
  let stageOffsetX = 0;
  let stageOffsetY = 0;

  switch (stageOrientation) {
    case StageOrientation.LEFT:
      stageOffsetX = STAGE_GRID_OFFSET;
      break;
    case StageOrientation.TOP:
      stageOffsetY = STAGE_GRID_OFFSET;
      break;
    case StageOrientation.RIGHT:
      maxX += STAGE_GRID_OFFSET;
      break;
    case StageOrientation.BOTTOM:
      maxY += STAGE_GRID_OFFSET;
      break;
    default:
      break;
  }

  if (minX || minY || stageOffsetX || stageOffsetY) {
    [...seats, ...nonSeats].forEach((data) => {
      data.xpos = (data.xpos - minX) + stageOffsetX;
      data.ypos = (data.ypos - minY) + stageOffsetY;
    });

    maxX -= minX;
    maxY -= minY;
  }

  return {
    seats,
    nonSeats,
    stageOrientation,
    columnCount: maxX + stageOffsetX + 1,
    rowCount: maxY + stageOffsetY + 1,
  };
}

class SyosSeatContainer extends SyosUiContainer {
  constructor(syosController) {
    super(syosController, '.tn-syos-seat-container');
    this.seats = null;

    this.$el.hide();
    this.$el.append(HTML);

    const $btnToggleFullscreen = this.find('.tn-syos-btn-toggle-full-screen');
    $btnToggleFullscreen.on('click', () => {
      this.syosController.toggleFullscreen();
    });

    this.bindMethods();

    this.syosController.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
    this.syosController.on(SyosEvents.LEGEND_DRAWER_TRANSITION_END, () => this.onLegendDrawerTransitionEnd());
  }

  bindMethods() {
    this.onScreenUpdated = this.onScreenUpdated.bind(this);
  }

  onScreenUpdated({ screen }) {
    if (this.pendingSeatListRequest) {
      this.pendingSeatListRequest.cancel();
    }

    if (screen) {
      this.show();
      this.load(screen.perf_no, screen.facility_no, screen.screen_no);
    } else {
      this.hide();
    }
  }

  onLegendDrawerTransitionEnd() {
    this.viewport.updateContainerRect();
  }

  load(perfNo, facilityNo, screenNo) {
    const dataService = this.syosController.getDataService();

    this.reset();

    const promise = dataService.getSeatList(perfNo, facilityNo, screenNo);

    promise
      .then((data) => {
        this.render(data);
      })
      .catch((reason) => {
        if (promise.canceled) {
          return;
        }
        throw reason;
      })
      .finally(() => {
        this.pendingSeatListRequest = null;
      });

    this.pendingSeatListRequest = promise;
  }

  render(apiData) {
    const {
      rowCount,
      stageOrientation,
      columnCount,
      seats: seatData,
      nonSeats: nonSeatData,
    } = adjustSeatCoordinates(apiData.seats);

    const cart = this.syosController.getCartContainer();
    const seats = new Map();
    const nonSeats = [];

    const seatRectProvider = new SeatClientRectProvider(this);

    seatData.forEach((data) => {
      // Grab existing SyosSeat instance from cart if present
      let seat = cart.getSeatForId(data.seat_no);
      if (seat === null) {
        seat = new SyosSeat(this.syosController, data, seatRectProvider);
      }
      seats.set(seat.id, seat);
    });

    nonSeatData.forEach((data) => {
      const x = SyosSeat.adjustValueForGrid(data.xpos);
      const y = SyosSeat.adjustValueForGrid(data.ypos);
      const letter = data.display_letter;

      nonSeats.push({ x, y, letter });
    });

    this.viewport = new Viewport({
      baseClassName: 'tn-syos-seat-container-viewport',
      drawTransformOrigin: SyosGlobalConfig.seatView.drawTransformOrigin,
      drawMargins: SyosGlobalConfig.seatView.drawMargins,
      zoomControl: {
        baseClassName: 'tn-syos-zoom-control',
      },
    });

    this.seatMap = new SyosSeatMap({
      rowCount,
      columnCount,
      unitSize: SyosSeat.GRID_UNIT_SIZE,
      marginSize: SyosSeat.GRID_MARGIN,
      seats: Array.from(seats.values()),
      nonSeats,
      stageOrientation,
    });

    const seatMapEl = this.seatMap.getEl();

    Array.from(seatMapEl.querySelectorAll(`.${SyosSeat.CSS_CLASS}`)).forEach((seatEl) => {
      const seatId = parseInt(seatEl.getAttribute('data-tn-seat-id'), 10);
      const seat = seats.get(seatId);
      seat.setEl(seatEl);
    });

    this.seats = seats;
    this.autoSelectSeats();

    this.$el.append(seatMapEl);

    this.$el.append(this.viewport.el);
    this.viewport.setContentEl(seatMapEl, this.seatMap.getWidth(), this.seatMap.getHeight());
    this.viewport.startListening();
  }

  autoSelectSeats() {
    // Debugging only
    if (autoSelectSeatQuantity > 0) {
      let countAutoSelected = 0;
      this.seats.forEach((seat) => {
        if (countAutoSelected >= autoSelectSeatQuantity) {
          return;
        }

        if (seat.getIsAvailable()) {
          countAutoSelected += 1;
          window.setTimeout(() => {
            seat.selectPriceType(seat.getPriceTypes()[0].price_type);
          });
        }
      });
    }
  }

  /**
   * @returns { import('./utils/SeatClientRectProvider').SeatClientRectContainerData }
   */
  getSeatClientRectContainerData() {
    const { viewport } = this;

    if (!viewport) {
      return {
        scale: 1,
        clientRect: {
          top: 0, bottom: 0, left: 0, right: 0, width: 0, height: 0,
        },
      };
    }

    return {
      scale: viewport.scale,
      clientRect: viewport.getContentClientRect(),
    };
  }

  reset() {
    if (this.seats) {
      this.seats.forEach(seat => seat.detach());
      this.seats = null;
    }

    if (this.seatMap) {
      this.seatMap.destroy();
      this.seatMap = null;
    }
    if (this.viewport) {
      this.viewport.detach();
      this.viewport.destroy();
      this.viewport = null;
    }
  }

  hide() {
    this.$el.hide();
    this.reset();
  }

  show() {
    this.$el.show();
  }
}

export default SyosSeatContainer;



// WEBPACK FOOTER //
// ./src/syos/SyosSeatContainer.js